import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { tutorApiClient } from '../../tutorApiClient';
import { TutorSearchResult, TutorSkill } from '../../models/Tutor';
import SEO from '../../components/SEO';
import { tutorListingPath } from '../../utils/searchPaths';
import { useTranslation } from 'react-i18next';
import { useExperimentalFeatures } from '../../contexts/ExperimentalFeaturesContext';
import { tutorSkillLabel } from '../../utils/tutorSkillI18n';
import { extractTutorProfileValidationMessage } from '../../utils/tutorSessionType';
import {
  buildTutorSearchPath,
  DEFAULT_OFFLINE_SESSION_TYPE,
  DEFAULT_OFFLINE_TUTOR_CITY,
  DEFAULT_TUTOR_SEARCH_MODE,
  defaultTutorSearchPath,
  isOfflineCityMissing,
  parseTutorSearchLocation,
  resolveOfflineSearchCity,
  type TutorOfflineSessionType,
  type TutorSearchMode,
} from '../../utils/tutorSearchMode';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import { tutorAvatarInitials } from '../../utils/tutorAvatarInitials';
import { resolveLanguageLabel } from '../../utils/languageOptions';
import TutorSessionDiscoveryMeta from '../../components/tutor/TutorSessionDiscoveryMeta';
import TutorSchoolAffiliation from '../../components/tutor/TutorSchoolAffiliation';
import TutorSearchToolbar from '../../components/tutor/TutorSearchToolbar';

function skillsMatch(a: TutorSkill[], b: TutorSkill[]): boolean {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((skill) => bSet.has(skill));
}

const TutorSearchResultsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { enabled: experimentalFeaturesEnabled } = useExperimentalFeatures();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<TutorSearchResult[]>([]);

  const {
    mode,
    city: cityParam,
    skills: skillsParam,
    offlineSessionType: offlineSessionTypeParam,
  } = useMemo(
    () => parseTutorSearchLocation(location.pathname, location.search),
    [location.pathname, location.search]
  );

  const [selectedMode, setSelectedMode] = useState<TutorSearchMode>(mode);
  const [selectedOfflineSessionType, setSelectedOfflineSessionType] =
    useState<TutorOfflineSessionType>(offlineSessionTypeParam);
  const [selectedCity, setSelectedCity] = useState<string>(cityParam);
  const [selectedSkills, setSelectedSkills] = useState<TutorSkill[]>(skillsParam);
  const [cityError, setCityError] = useState<string | null>(null);
  const [cities, setCities] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesError, setCitiesError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedMode(mode);
    setSelectedOfflineSessionType(offlineSessionTypeParam);
    setSelectedCity(cityParam);
    setSelectedSkills(skillsParam);
    setCityError(null);
  }, [mode, cityParam, skillsParam, offlineSessionTypeParam]);

  useEffect(() => {
    if (mode === 'OFFLINE' && isOfflineCityMissing(mode, cityParam)) {
      navigate(
        buildTutorSearchPath({
          mode: 'OFFLINE',
          city: DEFAULT_OFFLINE_TUTOR_CITY,
          skills: skillsParam,
          offlineSessionType: offlineSessionTypeParam,
        }),
        { replace: true }
      );
    }
  }, [mode, cityParam, skillsParam, offlineSessionTypeParam, navigate]);

  useEffect(() => {
    if (mode !== 'OFFLINE') {
      setCities([]);
      setCitiesError(null);
      setCitiesLoading(false);
      return;
    }

    let cancelled = false;
    const loadCities = async () => {
      setCitiesLoading(true);
      setCitiesError(null);
      try {
        const data = await tutorApiClient.listTutorCities(offlineSessionTypeParam);
        if (!cancelled) {
          setCities(data);
        }
      } catch (_e) {
        if (!cancelled) {
          setCitiesError(t('tutorSearch.citiesLoadError', 'Failed to load cities.'));
        }
      } finally {
        if (!cancelled) {
          setCitiesLoading(false);
        }
      }
    };
    void loadCities();
    return () => {
      cancelled = true;
    };
  }, [mode, offlineSessionTypeParam, t]);

  useEffect(() => {
    // Offline discovery requires a city; surface a non-destructive error
    // instead of issuing a request the backend would reject.
    if (isOfflineCityMissing(mode, cityParam)) {
      setResults([]);
      setError(t('tutorSearch.cityRequired', 'Select a city to search for in-person tutors.'));
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data =
          mode === 'OFFLINE'
            ? await tutorApiClient.searchOfflineTutors(cityParam, offlineSessionTypeParam)
            : await tutorApiClient.searchOnlineTutors();
        setResults(data);
      } catch (e) {
        // Keep the user's search inputs intact and surface the backend message.
        const backendMessage = extractTutorProfileValidationMessage(e);
        setError(backendMessage ?? t('tutorSearch.loadError', 'Failed to load tutors'));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [mode, cityParam, offlineSessionTypeParam, t]);

  const filteredResults = useMemo(() => {
    if (skillsParam.length === 0) {
      return results;
    }
    return results.filter((tutor) =>
      skillsParam.some((skill) => tutor.skills?.includes(skill))
    );
  }, [results, skillsParam]);

  const formatMoney = (amount: number, currency?: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency || 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  const lessonOptionPriceLabel = (tutor: TutorSearchResult) => {
    const cheapest = (tutor.lessonOptions ?? []).reduce<{ price: number; currency: string } | null>(
      (best, option) =>
        best == null || option.price < best.price
          ? { price: option.price, currency: option.lessonPriceCurrency ?? 'PLN' }
          : best,
      null
    );
    if (cheapest == null) return '—';
    return t('tutorSearch.fromLessonPrice', 'From {{price}} / lesson', {
      price: formatMoney(cheapest.price, cheapest.currency),
    });
  };

  const handleModeChange = (_event: React.MouseEvent<HTMLElement>, nextMode: TutorSearchMode | null) => {
    if (!nextMode || nextMode === selectedMode) return;

    if (nextMode === 'ONLINE') {
      setSelectedMode('ONLINE');
      setCityError(null);
      navigate(
        buildTutorSearchPath({
          mode: 'ONLINE',
          skills: selectedSkills,
        })
      );
      return;
    }

    const city = resolveOfflineSearchCity(selectedCity);
    setSelectedMode('OFFLINE');
    setSelectedCity(city);
    setCityError(null);
    navigate(
      buildTutorSearchPath({
        mode: 'OFFLINE',
        city,
        skills: selectedSkills,
        offlineSessionType: selectedOfflineSessionType,
      })
    );
  };

  const handleOfflineSessionTypeChange = (sessionType: TutorOfflineSessionType) => {
    if (sessionType === selectedOfflineSessionType && mode === 'OFFLINE') {
      return;
    }
    const city = resolveOfflineSearchCity(selectedCity);
    setSelectedOfflineSessionType(sessionType);
    setSelectedCity(city);
    setCityError(null);
    navigate(
      buildTutorSearchPath({
        mode: 'OFFLINE',
        city,
        skills: selectedSkills,
        offlineSessionType: sessionType,
      })
    );
  };

  const applyOfflineCitySearch = (city: string) => {
    const resolvedCity = resolveOfflineSearchCity(city);
    if (isOfflineCityMissing('OFFLINE', resolvedCity)) {
      setCityError(t('tutorSearch.cityRequired', 'Select a city to search for in-person tutors.'));
      return;
    }
    setCityError(null);
    if (
      mode === 'OFFLINE' &&
      resolvedCity === cityParam &&
      selectedOfflineSessionType === offlineSessionTypeParam
    ) {
      return;
    }
    navigate(
      buildTutorSearchPath({
        mode: 'OFFLINE',
        city: resolvedCity,
        skills: selectedSkills,
        offlineSessionType: selectedOfflineSessionType,
      })
    );
  };

  const handleSkillsChange = (skills: TutorSkill[]) => {
    setSelectedSkills(skills);
    if (skillsMatch(skills, skillsParam)) {
      return;
    }
    const city =
      selectedMode === 'OFFLINE' ? resolveOfflineSearchCity(selectedCity) : selectedCity;
    if (isOfflineCityMissing(selectedMode, city)) {
      setCityError(t('tutorSearch.cityRequired', 'Select a city to search for in-person tutors.'));
      return;
    }
    setCityError(null);
    navigate(
      buildTutorSearchPath({
        mode: selectedMode,
        city,
        skills,
        offlineSessionType: selectedOfflineSessionType,
      })
    );
  };

  const handleSearch = () => {
    const city =
      selectedMode === 'OFFLINE' ? resolveOfflineSearchCity(selectedCity) : selectedCity;
    if (isOfflineCityMissing(selectedMode, city)) {
      setCityError(t('tutorSearch.cityRequired', 'Select a city to search for in-person tutors.'));
      return;
    }
    setCityError(null);
    navigate(
      buildTutorSearchPath({
        mode: selectedMode,
        city,
        skills: selectedSkills,
        offlineSessionType: selectedOfflineSessionType,
      })
    );
  };

  const handleReset = () => {
    setSelectedMode(DEFAULT_TUTOR_SEARCH_MODE);
    setSelectedOfflineSessionType(DEFAULT_OFFLINE_SESSION_TYPE);
    setSelectedCity(DEFAULT_OFFLINE_TUTOR_CITY);
    setSelectedSkills([]);
    setCityError(null);
    navigate(defaultTutorSearchPath());
  };

  const renderReviewMeta = (tutor: TutorSearchResult) => {
    const reviewCount = tutor.reviewCount ?? 0;
    const avgReviewRating = tutor.avgReviewRating ?? 0;
    return (
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={0} sx={{ display: 'inline-flex' }}>
          {reviewCount > 0 ? (
            <StarRoundedIcon sx={{ color: '#F8C14A', fontSize: 18 }} />
          ) : (
            Array.from({ length: 5 }, (_, i) => (
              <StarBorderRoundedIcon key={i} sx={{ color: 'action.disabled', fontSize: 18 }} />
            ))
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {avgReviewRating.toFixed(2)} ({reviewCount} {t('reviews.reviews', 'reviews')})
        </Typography>
      </Stack>
    );
  };

  const handleOpenTutorListing = (tutor: TutorSearchResult) => {
    navigate(tutorListingPath(tutor.tutorId), {
      state: {
        tutor,
        mode,
        city: cityParam,
        offlineSessionType: offlineSessionTypeParam,
        searchBackPath: `${location.pathname}${location.search}`,
      },
    });
  };

  const cityOptions = useMemo(() => {
    const options = [...cities];
    if (selectedCity && !options.includes(selectedCity)) {
      options.unshift(selectedCity);
    }
    return options;
  }, [cities, selectedCity]);

  return (
    <>
      <SEO title={t('navigation.tutorSearch', 'Tutor Search')} noindex={true} />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, pb: { xs: 8, md: 10 } }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 2.5,
            border: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <TutorSearchToolbar
            selectedMode={selectedMode}
            onModeChange={handleModeChange}
            selectedOfflineSessionType={selectedOfflineSessionType}
            onOfflineSessionTypeChange={handleOfflineSessionTypeChange}
            selectedCity={selectedCity}
            onCityChange={(city) => {
              setSelectedCity(city);
              if (city.trim()) {
                setCityError(null);
              }
            }}
            onCitySelect={selectedMode === 'OFFLINE' ? applyOfflineCitySearch : undefined}
            cityError={cityError}
            citiesError={citiesError}
            cityOptions={cityOptions}
            citiesLoading={citiesLoading}
            selectedSkills={selectedSkills}
            onSkillsChange={handleSkillsChange}
            onSearch={handleSearch}
            onReset={handleReset}
            showTitle={!experimentalFeaturesEnabled}
            activeMode={mode}
            activeCity={cityParam}
            activeOfflineSessionType={offlineSessionTypeParam}
            activeSkills={skillsParam}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : filteredResults.length === 0 ? (
            <Typography color="text.secondary">{t('tutorSearch.empty', 'No tutors found.')}</Typography>
          ) : (
            <Grid container spacing={2}>
              {filteredResults.map((tutor) => (
                <Grid item xs={12} md={6} key={tutor.tutorId}>
                  <Card
                    variant="outlined"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleOpenTutorListing(tutor)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenTutorListing(tutor);
                      }
                    }}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: (theme) => theme.shadows[3],
                      },
                    }}
                  >
                    <Stack direction="row" spacing={3} alignItems="flex-start">
                      <Stack spacing={1} alignItems="center" sx={{ minWidth: { xs: 88, sm: 112 } }}>
                        <Avatar
                          src={tutor.profileImageUrl || undefined}
                          alt=""
                          variant="rounded"
                          sx={{
                            width: { xs: 88, sm: 112 },
                            height: { xs: 88, sm: 112 },
                            borderRadius: 2,
                            bgcolor: '#4AA7C6',
                            color: '#ffffff',
                            fontSize: { xs: '2rem', sm: '2.5rem' },
                          }}
                        >
                          {tutorAvatarInitials(tutor.name)}
                        </Avatar>
                        <Stack spacing={0.25} sx={{ width: '100%' }}>
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                            <EventAvailableRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {t('tutorSearch.statsConfirmedBookings', 'Bookings: {{count}}', {
                                count: tutor.bookingCount ?? 0,
                              })}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                            <GroupsRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {t('tutorSearch.statsStudents', 'Students: {{count}}', {
                                count: tutor.studentCount ?? 0,
                              })}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                      <Stack spacing={1.25} flex={1} minWidth={0}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="flex-start"
                          justifyContent="space-between"
                        >
                          <Box minWidth={0}>
                            <Typography variant="h6" sx={{ lineHeight: 1.25 }}>
                              {tutor.name}
                            </Typography>
                            <TutorSchoolAffiliation
                              organizationType={tutor.organizationType}
                              spaceId={tutor.spaceId}
                              spaceName={tutor.spaceName}
                              variant="subtitle2"
                            />
                            {(tutor.headline ?? '').trim() ? (
                              <Typography
                                variant="subtitle2"
                                color="text.secondary"
                                sx={{ lineHeight: 1.35, mt: 0.75, mb: 0.25 }}
                              >
                                {(tutor.headline ?? '').trim()}
                              </Typography>
                            ) : null}
                          </Box>
                          {(tutor.lessonOptions ?? []).length > 0 ? (
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label={lessonOptionPriceLabel(tutor)}
                              sx={{ fontWeight: 700, flexShrink: 0 }}
                            />
                          ) : null}
                        </Stack>
                        {renderReviewMeta(tutor)}
                        <TutorSessionDiscoveryMeta
                          supportedSessionTypes={tutor.supportedSessionTypes}
                          studentLessonCities={tutor.studentLessonCities}
                        />
                        {(tutor.skills ?? []).length > 0 ? (
                          <Box>
                            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 0.5 }}>
                              <MusicNoteRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                              <Typography variant="overline" color="text.secondary">
                                {t('tutorSearch.skills', 'Skills')}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              {tutor.skills.map((skill) => (
                                <Chip
                                  key={`${tutor.tutorId}-skill-${skill}`}
                                  size="small"
                                  label={tutorSkillLabel(t, skill)}
                                />
                              ))}
                            </Stack>
                          </Box>
                        ) : null}
                        {(tutor.languages ?? []).length > 0 ? (
                          <Box>
                            <Stack direction="row" spacing={0.55} alignItems="center" sx={{ mb: 0.5 }}>
                              <TranslateRoundedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                              <Typography variant="overline" color="text.secondary">
                                {t('tutorSearch.languages', 'Languages')}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              {tutor.languages.map((code) => (
                                <Chip
                                  key={`${tutor.tutorId}-lang-${code}`}
                                  size="small"
                                  label={resolveLanguageLabel(code, i18n.language)}
                                />
                              ))}
                            </Stack>
                          </Box>
                        ) : null}
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default TutorSearchResultsPage;
